import { useMemo, useState } from "react";

import { GripVertical, Loader2 } from "lucide-react";

import { DndContext, DragEndEvent, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CapabilityGate from "@/components/CapabilityGate";
import { useCapabilities } from "@/hooks/useCapabilities";
import { compactCityLabel, formatCityHeader } from "@/lib/geography";
import type { RoutePlan } from "@/types/routing";

type RoutingTimelineProps = {
  plan: RoutePlan | null;
  onReorder: (names: string[]) => void;
  onPin: (cityLabel: string) => void;
  needsRefresh: boolean;
  onRecalculate: () => void;
  onGenerateLead: (cityLabel: string, venueIds?: number[]) => void;
  leadStatus: Record<string, LeadGenerationStatus>;
};

type LeadGenerationStatus = "idle" | "loading" | "success" | "error";

export function RoutingTimeline({
  plan,
  onReorder,
  onPin,
  needsRefresh,
  onRecalculate,
  onGenerateLead,
  leadStatus,
}: RoutingTimelineProps) {
  const timelineItems = useMemo(() => {
    if (!plan) return [];
    const cityMap = new Map(
      (plan.cities || []).map((city) => [compactCityLabel(city.city, city.state), city]),
    );
    const windowMap = new Map(
      (plan.city_windows || []).map((window) => [compactCityLabel(window.city, window.state), window]),
    );
    return (plan.ordered_city_names || []).map((label, index) => {
      const [rawCity, rawState = ""] = label.split(",").map((value) => value.trim());
      const compactLabel = compactCityLabel(rawCity, rawState);
      const cityRecord = cityMap.get(compactLabel);
      const indexedWindow = plan.city_windows?.[index];
      const window =
        windowMap.get(compactLabel) ||
        (indexedWindow &&
        compactCityLabel(indexedWindow.city, indexedWindow.state) === compactLabel
          ? indexedWindow
          : undefined);
      const displayLabel = formatCityHeader(cityRecord?.city ?? rawCity, cityRecord?.state ?? rawState);
      const venues = (cityRecord?.venues ?? []).map((venue, venueIndex) => ({
        id: venue.id,
        name:
          venue.name ||
          venue.company_name ||
          cityRecord?.venue_names?.[venueIndex] ||
          `Venue ${venueIndex + 1}`,
        email: venue.email,
        phone: venue.phone,
        score: venue.last_booking_score,
        tags: venue.tags ?? [],
      }));
      const venueCount = venues.length || (cityRecord?.venue_ids?.length ?? 0);
      return {
        id: label,
        label: displayLabel,
        compactLabel,
        window,
        venues,
        pinned: Boolean(plan.pinned_cities?.[index]),
        hasVenues: venueCount > 0,
      };
    });
  }, [plan]);

  const items = timelineItems.map((item) => item.id);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !plan) return;
    const current = [...items];
    const oldIndex = current.indexOf(active.id as string);
    const newIndex = current.indexOf(over.id as string);
    current.splice(oldIndex, 1);
    current.splice(newIndex, 0, active.id as string);
    onReorder(current);
  };

  return (
    <Card className="aa-glass-card rounded-2xl">
      <CardHeader className="flex items-center justify-between px-6 pt-6 pb-4">
        <CardTitle className="text-lg font-semibold tracking-wide text-foreground/90">Timeline</CardTitle>
        {needsRefresh ? (
          <Button size="sm" variant="outline" onClick={onRecalculate}>
            Recalculate
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="px-6 pb-6">
        {timelineItems.length ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items} strategy={horizontalListSortingStrategy}>
              <div className="flex snap-x gap-4 overflow-x-auto pb-2">
                {timelineItems.map((item, index) => (
                  <SortableCard
                    key={item.id}
                    id={item.id}
                    displayLabel={item.label}
                    cityLabel={item.compactLabel}
                    window={item.window}
                    index={index}
                    pinned={item.pinned}
                    venues={item.venues}
                    onPin={() => onPin(item.id)}
                    onGenerateLead={(venueIds) => onGenerateLead(item.compactLabel, venueIds)}
                    leadState={leadStatus[item.compactLabel] ?? "idle"}
                    hasVenues={item.hasVenues}
                    needsRefresh={needsRefresh}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">Add routing parameters to begin.</div>
        )}
      </CardContent>
    </Card>
  );
}

type SortableCardProps = {
  id: string;
  displayLabel: string;
  cityLabel: string;
  window?: RoutePlan["city_windows"][number];
  index: number;
  pinned: boolean;
  venues: Array<{
    id: number;
    name: string;
    email?: string | null;
    phone?: string | null;
    score?: number | null;
    tags: string[];
  }>;
  hasVenues: boolean;
  leadState: LeadGenerationStatus;
  onPin: () => void;
  onGenerateLead: (venueIds?: number[]) => void;
  needsRefresh: boolean;
};

function SortableCard({
  id,
  displayLabel,
  cityLabel,
  window,
  index,
  pinned,
  venues,
  hasVenues,
  leadState,
  onPin,
  onGenerateLead,
  needsRefresh,
}: SortableCardProps) {
  const [open, setOpen] = useState(false);
  const [selectedVenues, setSelectedVenues] = useState<Set<number>>(new Set());
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({ id });
  const { openUpgradeModal } = useCapabilities();
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const arrival = formatDate(window?.arrival_date);
  const bookingStart = formatDate(window?.booking_range_start);
  const bookingEnd = formatDate(window?.booking_range_end);

  const leadLabel =
    leadState === "success"
      ? "Leads queued"
      : leadState === "error"
        ? "Retry lead sync"
        : leadState === "loading"
          ? "Generating…"
          : "Generate Lead";
  const leadDisabled = leadState === "loading" || !hasVenues || needsRefresh;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="min-w-[200px] snap-start rounded-2xl border border-border/20 bg-background/35 p-4 text-sm text-foreground shadow-sm"
    >
      <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
        <span>Stop {index + 1}</span>
        <div className="flex items-center gap-2">
          <CapabilityGate flag="canUseRoutingAI" variant="hide">
            {pinned ? <span className="text-amber-300">Pinned</span> : null}
          </CapabilityGate>
          <button
            type="button"
            ref={setActivatorNodeRef}
            {...listeners}
            {...attributes}
            className="rounded-md border border-border/5 bg-background/5 p-1 text-foreground/70 transition-colors hover:bg-background/10"
            aria-label="Reorder stop"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="mt-2 text-lg font-semibold text-foreground">{displayLabel}</p>
      <CapabilityGate
        flag="canUseRoutingOsrm"
        fallback={<p className="mt-3 text-xs text-muted-foreground">Upgrade to view arrival and hold windows.</p>}
      >
        <div className="mt-3 text-xs text-foreground/70">
          {arrival ? (
            <>
              <p className="text-sm font-semibold text-foreground">Arrival</p>
              <p className="text-foreground/80">{arrival}</p>
              <p className="mt-2 text-sm font-semibold text-foreground">Hold window</p>
              <p className="text-foreground/80">
                {bookingStart || "TBD"} – {bookingEnd || "TBD"}
              </p>
            </>
          ) : (
            <p className="text-foreground/60">No holds assigned.</p>
          )}
        </div>
      </CapabilityGate>
      <CapabilityGate
        flag="canUseRoutingAI"
        fallback={
          venues.length ? (
            <p className="mt-4 text-xs text-muted-foreground">Upgrade to view venue lists for this stop.</p>
          ) : null
        }
      >
        {venues.length ? (
          <div className="mt-4 rounded-xl border border-border/5 bg-[#0d101d]/70 p-3 text-xs text-foreground/70">
            <div className="flex items-center justify-between">
              <p className="font-semibold">Venues ({venues.length})</p>
              <Button size="sm" variant="ghost" className="text-primary-foreground dark:text-amber-200" onClick={() => setOpen((value) => !value)}>
                {open ? "Hide" : "Show"}
              </Button>
            </div>
            {open ? (
              <ul className="mt-2 space-y-1">
                {venues.map((venue) => {
                  const checked = selectedVenues.has(venue.id);
                  return (
                    <li
                      key={`${cityLabel}-${venue.id}`}
                      className={`rounded-md border px-2 py-2 ${
                        checked ? "border-amber-300/50 bg-amber-400/10" : "border-border/5 bg-background/5"
                      }`}
                    >
                      <label className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={checked}
                          onChange={() => {
                            setSelectedVenues((prev) => {
                              const next = new Set(prev);
                              if (next.has(venue.id)) {
                                next.delete(venue.id);
                              } else {
                                next.add(venue.id);
                              }
                              return next;
                            });
                          }}
                        />
                        <div className="flex-1 space-y-1">
                          <p className="font-semibold text-foreground">{venue.name}</p>
                          {typeof venue.score === "number" ? (
                            <p className="text-foreground/70">Score {Number(venue.score).toFixed(2)}</p>
                          ) : null}
                          {venue.tags.length ? (
                            <p className="text-muted-foreground">
                              Tags: {venue.tags.slice(0, 3).join(", ")}
                              {venue.tags.length > 3 ? "…" : ""}
                            </p>
                          ) : null}
                          {venue.email ? <p className="text-muted-foreground">{venue.email}</p> : null}
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : null}
      </CapabilityGate>
      <CapabilityGate
        flag="canUseRoutingAI"
        fallback={
          <Button size="sm" variant="ghost" className="mt-4 w-full" onClick={() => openUpgradeModal("AI routing")}>
            Unlock pinning
          </Button>
        }
      >
        <Button size="sm" variant="ghost" className="mt-4 w-full" onClick={onPin}>
          {pinned ? "Pinned" : "Lock stop"}
        </Button>
      </CapabilityGate>
      <CapabilityGate
        flag="canGenerateRoutingLeads"
        fallback={
          <Button size="sm" className="mt-2 w-full" onClick={() => openUpgradeModal("Routing lead sync")}>
            Unlock lead sync
          </Button>
        }
      >
        <div className="mt-2 space-y-2">
          <Button
            size="sm"
            className="w-full"
            disabled={leadDisabled}
            variant={leadState === "success" ? "secondary" : "default"}
            onClick={() => {
              onGenerateLead();
              setSelectedVenues(new Set());
            }}
          >
            {leadState === "loading" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {leadLabel === "Generate Lead" ? "Add all to booking" : leadLabel}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="w-full"
            disabled={!selectedVenues.size || leadState === "loading" || needsRefresh}
            onClick={() => {
              onGenerateLead(Array.from(selectedVenues));
              setSelectedVenues(new Set());
            }}
          >
            Send selected {selectedVenues.size ? `(${selectedVenues.size})` : ""}
          </Button>
        </div>
      </CapabilityGate>
      {needsRefresh ? (
        <p className="mt-2 text-xs text-amber-200">Recalculate the route before syncing leads.</p>
      ) : null}
      {!hasVenues ? <p className="mt-1 text-xs text-primary-foreground dark:text-amber-200">No CRM venues synced for this city.</p> : null}
    </div>
  );
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
